import { useConnectionStore } from '../../connections/store/connectionStore';
import { coerceDatabaseToConnectionLimit } from '../../connections/utils/connectionDatabaseLimit';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { getDatabaseFromNodeId } from '../../explorer/utils/nodeIdUtils';
import { toast } from '../../../components/ui/toaster';
import { openDataImportTab } from './openDataImportTab';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';
import { dataImportUnsupportedReason, resolveDataImportConnection } from './mapping/targetEligibility';

/**
 * Opens the Data Import wizard for the connection currently selected in the
 * Object Explorer, falling back to a connected connection when nothing is
 * selected (2+ connections, no tree click — INJ-2; `resolveDataImportConnection`).
 * Returns true if a tab opened, false if guarded out (no connection at all, or
 * the resolved connection's engine is Kusto or Azure Storage — no SQL table to
 * import into, `dataImportUnsupportedReason`).
 *
 * Used by the two surfaces that act on the globally-selected connection
 * (main toolbar "Import Data…" and the Ctrl+I / Cmd+I shortcut) — the two
 * context menus act on the clicked node instead and resolve connectionInfo
 * from node metadata directly. Callers fire their own analytics; this
 * function does not.
 */
export function openDataImportForActiveConnection(): boolean {
  const { selectedConnectionId, selectedDatabase } = useDatabaseUIStore.getState();
  const { connections, activeConnections } = useConnectionStore.getState();
  // The "selected, else first connected" fallback AiToolbarButton's
  // getSelectedConnection already uses, preferring a connection that can be an
  // import target: a user with 2+ connections and no tree selection still gets
  // a concrete target instead of a dead action. Safe because this only seeds
  // the wizard's Target step — nothing writes until the user confirms the
  // destination connection/database there.
  const activeConnection = resolveDataImportConnection(selectedConnectionId, activeConnections);
  const connection = connections.find((c) => c.id === activeConnection?.id);
  if (!activeConnection || !connection) {
    toast.error({ title: 'No database selected', description: 'Connect to a database first to import data.' });
    return false;
  }
  const unsupported = dataImportUnsupportedReason(activeConnection.connection.engine);
  if (unsupported) {
    toast.error({ title: 'Not available', description: `${unsupported}.` });
    return false;
  }
  // Coercion chokepoint (mirrors the AI-workspace export gate): the Object
  // Explorer selection is stored uncoerced and can go stale across a limit
  // edit, so an off-limit database must never seed an import target.
  const requested = selectedDatabase
    ? (getDatabaseFromNodeId(selectedDatabase) ?? selectedDatabase)
    : activeConnection.connection.database;
  const database = coerceDatabaseToConnectionLimit(connection, requested);
  openDataImportTab({
    connectionInfo: {
      connectionId: activeConnection.id,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database,
    },
  });
  return true;
}
